import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, CheckCircle, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TankTailoredBundle = () => {
  const [submitted, setSubmitted] = useState(false);
  const [bundleId, setBundleId] = useState(null);
  const { register, handleSubmit, formState: { errors } } = useForm();
  const [lightingLevel, setLightingLevel] = useState('');

  const onSubmit = async (data) => {
    try {
      const requestData = {
        ...data,
        lighting_level: lightingLevel
      };
      
      const response = await axios.post(`${API}/tank-tailored-bundle`, requestData);
      setBundleId(response.data.request_id);
      setSubmitted(true);
      toast.success('Tank bundle request submitted!');
    } catch (error) {
      console.error('Error submitting bundle request:', error);
      toast.error('Failed to submit request');
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen py-16" data-testid="bundle-success-page">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-3xl">
          <Card className="p-12 text-center">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-primary" />
            </div>
            <h1 className="heading-font text-4xl font-bold mb-4">Request Submitted!</h1>
            <p className="text-lg text-muted-foreground mb-8">
              We'll carefully curate 6 plant bunches perfect for your tank setup and contact you within 24 hours to complete your order.
            </p>
            <div className="bg-secondary/50 rounded-lg p-6 mb-8">
              <p className="text-sm text-muted-foreground mb-2">Request ID</p>
              <p className="text-xl font-bold mono-font" data-testid="bundle-request-id">{bundleId}</p>
            </div>
            <Button onClick={() => window.location.href = '/'} className="rounded-full">Back to Home</Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-16" data-testid="tank-tailored-page">
      <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-5xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-semibold uppercase tracking-wider">Expert Curation</span>
          </div>
          <h1 className="heading-font text-5xl md:text-6xl font-bold mb-4" data-testid="bundle-title">Tank Tailored Bundle</h1>
          <p className="text-2xl font-bold text-primary mb-4">$34.99</p>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            6 hand-selected plant bunches perfectly suited to your tank setup. Each stem plant bunch contains 10 or more stems. Includes at least one bunch of red stem plants. Curated by our expert growers based on your tank size, lighting, and inhabitants.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          <Card className="p-6">
            <h3 className="heading-font text-xl font-semibold mb-4">What's Included</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <span>6 bunches of hand-selected plants</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <span>Each stem bunch contains 10+ stems</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <span>At least one bunch of red stem plants</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <span>Mix of foreground, midground, and background</span>
              </li>
            </ul>
          </Card>

          <Card className="p-6">
            <h3 className="heading-font text-xl font-semibold mb-4">How It Works</h3>
            <ol className="space-y-3 text-sm">
              <li className="flex gap-2">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-xs">1</span>
                <span>Tell us about your tank setup</span>
              </li>
              <li className="flex gap-2">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-xs">2</span>
                <span>We hand-pick 6 perfect bunches</span>
              </li>
              <li className="flex gap-2">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-xs">3</span>
                <span>We email you to complete checkout</span>
              </li>
              <li className="flex gap-2">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-xs">4</span>
                <span>Your bundle ships fresh - $34.99 flat rate</span>
              </li>
            </ol>
          </Card>
        </div>

        <Card className="p-8">
          <h2 className="heading-font text-2xl font-semibold mb-6">Tell Us About Your Tank</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name">Your Name *</Label>
                <Input
                  id="name"
                  {...register('name', { required: 'Name is required' })}
                  className="mt-2"
                  data-testid="bundle-name-input"
                />
                {errors.name && <p className="text-destructive text-sm mt-1">{errors.name.message}</p>}
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email', { required: 'Email is required' })}
                  className="mt-2"
                  data-testid="bundle-email-input"
                />
                {errors.email && <p className="text-destructive text-sm mt-1">{errors.email.message}</p>}
              </div>
            </div>

            <div>
              <Label htmlFor="tank_size">Tank Size (gallons) *</Label>
              <Input
                id="tank_size"
                type="number"
                {...register('tank_size', { required: 'Tank size is required', min: 1 })}
                className="mt-2"
                placeholder="e.g., 20, 55, 75"
                data-testid="bundle-tank-size-input"
              />
              {errors.tank_size && <p className="text-destructive text-sm mt-1">{errors.tank_size.message}</p>}
            </div>

            <div>
              <Label htmlFor="lighting_wattage">Lighting Wattage (optional)</Label>
              <Input
                id="lighting_wattage"
                {...register('lighting_wattage')}
                className="mt-2"
                placeholder="e.g., 24W LED"
                data-testid="bundle-wattage-input"
              />
            </div>

            <div>
              <Label htmlFor="lighting_level">Light Intensity *</Label>
              <Select onValueChange={setLightingLevel} required>
                <SelectTrigger className="mt-2" data-testid="bundle-lighting-select">
                  <SelectValue placeholder="Select light intensity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
              {!lightingLevel && <p className="text-sm text-muted-foreground mt-1">Please select your lighting intensity</p>}
            </div>

            <div>
              <Label htmlFor="substrate_type">Substrate Type *</Label>
              <Select onValueChange={(value) => register('substrate_type').onChange({ target: { value } })} required>
                <SelectTrigger className="mt-2" data-testid="bundle-substrate-select">
                  <SelectValue placeholder="Select substrate type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sand">Sand Only</SelectItem>
                  <SelectItem value="dirt_capped_sand">Dirt Capped with Sand</SelectItem>
                  <SelectItem value="bare">Bare Bottom</SelectItem>
                  <SelectItem value="aquasoil">Aquasoil</SelectItem>
                  <SelectItem value="gravel">Gravel</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="filter_type">Filter Type (optional)</Label>
              <Input
                id="filter_type"
                {...register('filter_type')}
                className="mt-2"
                placeholder="e.g., HOB, canister, sponge, internal"
                data-testid="bundle-filter-input"
              />
            </div>

            <div>
              <Label htmlFor="water_flow">Water Flow Strength (optional)</Label>
              <Select onValueChange={(value) => register('water_flow').onChange({ target: { value } })}>
                <SelectTrigger className="mt-2" data-testid="bundle-flow-select">
                  <SelectValue placeholder="Select water flow strength" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="tank_inhabitants">Tank Inhabitants (optional)</Label>
              <Textarea
                id="tank_inhabitants"
                {...register('tank_inhabitants')}
                className="mt-2"
                placeholder="e.g., Neocaridina shrimp, Betta fish, community tank with tetras and corydoras"
                rows={4}
                data-testid="bundle-inhabitants-input"
              />
            </div>

            <div>
              <Label htmlFor="additional_notes">Additional Notes (optional)</Label>
              <Textarea
                id="additional_notes"
                {...register('additional_notes')}
                className="mt-2"
                placeholder="Any specific preferences, colors you like, or plants to avoid?"
                rows={3}
                data-testid="bundle-notes-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-14 rounded-full font-medium text-lg"
              data-testid="submit-bundle-btn"
            >
              Submit Tank Bundle Request - $34.99
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default TankTailoredBundle;